import React, { useRef, useEffect } from "react";

import {
  FilterContainer,
  DropdownContainer,
  DropdownContent,
  ChannelOptionContainer,
  ChannelIconContainer,
  ChannelText,
  StyledCheckbox,
  ChannelContentWrapper,
  StyledChannelIcon,
} from "./styles";
import FilterDropDownBtn from "./FilterDropDownBtn";

export interface ChannelOption {
  key: string;
  label: string;
  icon: React.ReactElement;
}

interface ChannelFilterProps {
  label: string;
  channels: ChannelOption[];
  selectedChannels: string[];
  onSelect: (keys: string[]) => void;
  isOpen: boolean;
  onToggle: () => void;
}

const ChannelFilter: React.FC<ChannelFilterProps> = ({
  label,
  channels,
  selectedChannels,
  onSelect,
  isOpen,
  onToggle,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onToggle();
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle]);

  // Toggle channel selection
  const handleChannelClick = (key: string) => {
    let newSelected: string[];
    if (selectedChannels.includes(key)) {
      newSelected = selectedChannels.filter((k) => k !== key);
    } else {
      newSelected = [...selectedChannels, key];
    }
    onSelect(newSelected);
  };

  // Check if filter is active (has selected channels)
  const isActive = selectedChannels.length > 0;

  return (
    <FilterContainer ref={dropdownRef}>
      <FilterDropDownBtn label={label} onClick={onToggle} active={isActive} />
      {/* Dropdown content: Channel filter */}
      {isOpen && (
        <DropdownContainer>
          <DropdownContent>
            {channels.map((channel) => {
              const checked = selectedChannels.includes(channel.key);
              return (
                <ChannelOptionContainer
                  key={channel.key}
                  onClick={() => handleChannelClick(channel.key)}
                  checked={checked}
                >
                  {/* Icon and text on the left */}
                  <ChannelContentWrapper>
                    <ChannelIconContainer>
                      <StyledChannelIcon>
                        {React.cloneElement(channel.icon, {})}
                      </StyledChannelIcon>
                    </ChannelIconContainer>
                    <ChannelText checked={checked}>{channel.label}</ChannelText>
                  </ChannelContentWrapper>
                  {/* MUI Checkbox on the right */}
                  <StyledCheckbox
                    checked={checked}
                    color="primary"
                    size="small"
                    onChange={() => handleChannelClick(channel.key)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </ChannelOptionContainer>
              );
            })}
          </DropdownContent>
        </DropdownContainer>
      )}
    </FilterContainer>
  );
};

export default ChannelFilter;
